import * as React from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import axios from "axios";

export function ResetPasswordForm() {
  const [newPassword, setNewPassword] = React.useState<string>("");
  const [confirmPassword, setConfirmPassword] = React.useState<string>("");
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [passwordValidations, setPasswordValidations] = React.useState({
    length: false,
    uppercase: false,
    number: false,
    specialChar: false,
  });
  const { token } = useParams<{ token: string }>(); 
  const navigate = useNavigate();

  const resetPassword = async (token: string | undefined, newPassword: string): Promise<void> => {
    try {
      const response = await axios.put(`/common/auth/reset-password/${token}`, {
        newPassword,
      });
      return response.data; // Vous pouvez traiter la réponse si nécessaire
    } catch (error) {
      console.error("Erreur lors de la réinitialisation du mot de passe", error);
      throw new Error("Échec de la réinitialisation du mot de passe.");
    }
  };

  const handlePasswordChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setNewPassword(value);

    // Vérifier les conditions du mot de passe
    setPasswordValidations({
      length: value.length >= 12,
      uppercase: /[A-Z]/.test(value),
      number: /\d/.test(value),
      specialChar: /[!@#?$%^&*]/.test(value),
    });
  };

  const handleConfirmPasswordChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setConfirmPassword(event.target.value);
  };

  const handleSubmit = async (event: React.SyntheticEvent) => {
    event.preventDefault();

    // Vérifier si les deux mots de passe sont identiques
    if (newPassword !== confirmPassword) {
      toast.error("Les mots de passe ne correspondent pas.");
      return;
    }

    // Vérifier si toutes les conditions du mot de passe sont remplies
    if (
      !passwordValidations.length ||
      !passwordValidations.uppercase ||
      !passwordValidations.number ||
      !passwordValidations.specialChar
    ) {
      toast.error("Le mot de passe ne respecte pas toutes les conditions.");
      return;
    }

    setIsLoading(true);

    try {
      await resetPassword(token, newPassword);
      toast.success("Mot de passe réinitialisé avec succès.");
      navigate("/connexion"); // Redirigez l'utilisateur vers la page de connexion après la réinitialisation
    } catch (error) {
      toast.error("Erreur lors de la réinitialisation du mot de passe.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="grid gap-6">
      <form onSubmit={handleSubmit}>
        <div className="grid gap-1">
          <Label htmlFor="password">Entrez votre nouveau mot de passe</Label>
          <Input
            id="password"
            type="password"
            placeholder="Nouveau mot de passe"
            value={newPassword}
            onChange={handlePasswordChange}
            disabled={isLoading}
          />
          <PasswordRequirements validations={passwordValidations} />
        </div>

        <div className="grid gap-1 mt-6">
          <Label htmlFor="confirmPassword">Confirmez votre nouveau mot de passe</Label>
          <Input
            id="confirmPassword"
            type="password"
            placeholder="Confirmez votre nouveau mot de passe"
            value={confirmPassword}
            onChange={handleConfirmPasswordChange}
            disabled={isLoading}
          />
        </div>

        <Button type="submit" disabled={isLoading} className="mt-6">
          {isLoading ? "En cours..." : "Réinitialiser le mot de passe"}
        </Button>
      </form>
    </div>
  );
}

type Validations = {
  length: boolean;
  uppercase: boolean;
  number: boolean;
  specialChar: boolean;
};

const PasswordRequirements = ({ validations }: { validations: Validations }) => {
  return (
    <div className="mt-2">
      <ul className="text-sm text-gray-600 items-start flex flex-col">
        <li className={validations.length ? "text-green-500" : "text-red-500"}>
          {validations.length ? "✔" : "✖"} Minimum 12 caractères
        </li>
        <li className={validations.uppercase ? "text-green-500" : "text-red-500"}>
          {validations.uppercase ? "✔" : "✖"} Une majuscule
        </li>
        <li className={validations.number ? "text-green-500" : "text-red-500"}>
          {validations.number ? "✔" : "✖"} Un chiffre
        </li>
        <li className={validations.specialChar ? "text-green-500" : "text-red-500"}>
          {validations.specialChar ? "✔" : "✖"} Un caractère spécial (!@#?$%^&*)
        </li>
      </ul>
    </div>
  );
};
